import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type InterfaceMode = "essential" | "advanced";
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";
export type TextSize = "normal" | "large";

type UiPreferences = {
  mode: InterfaceMode;
  setMode(mode: InterfaceMode): void;
  toggleMode(): void;
  theme: ThemePreference;
  setTheme(theme: ThemePreference): void;
  resolvedTheme: ResolvedTheme;
  textSize: TextSize;
  setTextSize(textSize: TextSize): void;
};

const MODE_STORAGE_KEY = "cochegest:interface-mode";
const THEME_STORAGE_KEY = "cochegest:theme";
const TEXT_SIZE_STORAGE_KEY = "cochegest:text-size";

const UiPreferencesContext = createContext<UiPreferences | null>(null);

function storedMode(): InterfaceMode {
  try {
    return window.localStorage.getItem(MODE_STORAGE_KEY) === "advanced" ? "advanced" : "essential";
  } catch {
    return "essential";
  }
}

function storedTheme(): ThemePreference {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "dark" || value === "light" ? value : "system";
  } catch {
    return "system";
  }
}

function storedTextSize(): TextSize {
  try {
    return window.localStorage.getItem(TEXT_SIZE_STORAGE_KEY) === "large" ? "large" : "normal";
  } catch {
    return "normal";
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function UiPreferencesProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<InterfaceMode>(storedMode);
  const [theme, setTheme] = useState<ThemePreference>(storedTheme);
  const [textSize, setTextSize] = useState<TextSize>(storedTextSize);
  const [systemResolved, setSystemResolved] = useState<ResolvedTheme>(systemTheme);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setSystemResolved(media.matches ? "dark" : "light");
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemResolved : theme;

  useEffect(() => {
    document.documentElement.dataset.interfaceMode = mode;
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {
      // La preferencia sigue activa durante la sesión aunque el navegador bloquee el almacenamiento.
    }
  }, [mode]);

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Igual que el modo de interfaz: la sesión conserva la preferencia aunque falle el almacenamiento.
    }
  }, [resolvedTheme, theme]);

  useEffect(() => {
    document.documentElement.dataset.textSize = textSize;
    try {
      window.localStorage.setItem(TEXT_SIZE_STORAGE_KEY, textSize);
    } catch {
      // La preferencia de lectura se conserva durante la sesión.
    }
  }, [textSize]);

  const value = useMemo<UiPreferences>(() => ({
    mode,
    setMode,
    toggleMode: () => setMode((current) => current === "essential" ? "advanced" : "essential"),
    theme,
    setTheme,
    resolvedTheme,
    textSize,
    setTextSize,
  }), [mode, theme, resolvedTheme, textSize]);

  return <UiPreferencesContext.Provider value={value}>{children}</UiPreferencesContext.Provider>;
}

export function useUiPreferences(): UiPreferences {
  const value = useContext(UiPreferencesContext);
  if (!value) throw new Error("UI_PREFERENCES_PROVIDER_MISSING: falta el proveedor de preferencias de interfaz");
  return value;
}
